/* P15 elasticity: position the existing marker; never replace or resize images. */
(() => {
  'use strict';
  if (customElements.get('ra-elasticita-photo')) return;
  class RaElasticitaPhoto extends HTMLElement {
    connectedCallback() {
      if (this._connected || !this.closest('[data-section-type="product-page"]')) return;
      this._host = this.parentElement;
      if (!this._host || this._host.tagName !== 'RESPONSIVE-IMAGE') return;
      this._connected = true;
      this._queue = () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = 0;
          if (this.isConnected) this._place();
        });
      };
      if (getComputedStyle(this._host).position === 'static') this._host.style.position = 'relative';
      this._resize = typeof ResizeObserver === 'function' ? new ResizeObserver(this._queue) : null;
      if (this._resize) this._resize.observe(this._host);
      this._mutations = new MutationObserver(records => {
        if (records.some(r => r.target === this._host || r.target === this._img)) this._queue();
      });
      this._mutations.observe(this._host, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['src', 'srcset', 'sizes', 'style', 'class']
      });
      window.addEventListener('resize', this._queue, { passive: true });
      this._queue();
    }
    disconnectedCallback() {
      if (!this._connected) return;
      cancelAnimationFrame(this._raf);
      this._raf = 0;
      if (this._resize) this._resize.disconnect();
      this._mutations.disconnect();
      window.removeEventListener('resize', this._queue);
      this._unbindImage();
      this._connected = false;
      this.removeAttribute('data-ra-ready');
    }
    _unbindImage() {
      if (!this._img) return;
      this._img.removeEventListener('load', this._queue);
      this._img.removeEventListener('error', this._queue);
      if (this._resize) this._resize.unobserve(this._img);
      this._img = null;
    }
    _place() {
      const img = this._host.querySelector('img');
      if (img !== this._img) {
        this._unbindImage();
        this._img = img;
        if (img) {
          img.addEventListener('load', this._queue);
          img.addEventListener('error', this._queue);
          if (this._resize) this._resize.observe(img);
        }
      }
      if (!img || !img.complete || !img.naturalWidth) {
        this.removeAttribute('data-ra-ready');
        return;
      }
      const h = this._host.getBoundingClientRect();
      const r = img.getBoundingClientRect();
      if (!h.width || !h.height || !r.width || !r.height) {
        this.removeAttribute('data-ra-ready');
        return;
      }
      const sx = h.width / this._host.offsetWidth || 1;
      const sy = h.height / this._host.offsetHeight || 1;
      const cs = getComputedStyle(img);
      const n = v => parseFloat(v) || 0;
      const x0 = r.left + (n(cs.borderLeftWidth) + n(cs.paddingLeft)) * sx;
      const y0 = r.top + (n(cs.borderTopWidth) + n(cs.paddingTop)) * sy;
      const bw = r.width - (n(cs.borderLeftWidth) + n(cs.borderRightWidth) + n(cs.paddingLeft) + n(cs.paddingRight)) * sx;
      const bh = r.height - (n(cs.borderTopWidth) + n(cs.borderBottomWidth) + n(cs.paddingTop) + n(cs.paddingBottom)) * sy;
      let ow = bw, oh = bh;
      if (['contain', 'scale-down', 'none'].includes(cs.objectFit)) {
        let scale = cs.objectFit === 'none' ? 1 : Math.min(bw / sx / img.naturalWidth, bh / sy / img.naturalHeight);
        if (cs.objectFit === 'scale-down') scale = Math.min(1, scale);
        ow = img.naturalWidth * scale * sx;
        oh = img.naturalHeight * scale * sy;
      }
      const pos = cs.objectPosition.trim().split(/\s+/);
      const offset = (v, space, scale) => {
        if (!v || v === 'center') return space / 2;
        if (v === 'left' || v === 'top') return 0;
        if (v === 'right' || v === 'bottom') return space;
        return v.endsWith('%') ? space * n(v) / 100 : n(v) * scale;
      };
      const ox = offset(pos[0], bw - ow, sx);
      const oy = offset(pos[1], bh - oh, sy);
      const left = Math.max(h.left, x0, x0 + ox);
      const right = Math.min(h.right, x0 + bw, x0 + ox + ow);
      const bottom = Math.min(h.bottom, y0 + bh, y0 + oy + oh);
      const inset = n(getComputedStyle(this).getPropertyValue('--ra-photo-inset')) || 8;
      if (right <= left || bottom <= y0) {
        this.removeAttribute('data-ra-ready');
        return;
      }
      this.style.left = Math.max(0, (left - h.left) / sx - this._host.clientLeft + inset) + 'px';
      this.style.bottom = Math.max(0, (h.bottom - bottom) / sy - this._host.clientTop + inset) + 'px';
      this.style.maxWidth = Math.max(0, (right - left) / sx - 2 * inset) + 'px';
      this.setAttribute('data-ra-ready', '');
    }
  }
  customElements.define('ra-elasticita-photo', RaElasticitaPhoto);
})();
